import { Component } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

/**
 * Global error boundary. Wraps the entire app so any uncaught React error
 * inside a page/component renders a friendly fallback instead of blanking
 * the whole screen. Production builds don't show the dev overlay, so
 * without this, a single component throw = white page.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
    this.setState({ info });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, info: null });
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="grid min-h-screen place-items-center bg-slate-50 p-6" data-testid="error-boundary">
        <div className="max-w-lg rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-amber-100 text-amber-600">
            <AlertTriangle className="h-7 w-7" />
          </div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Something went wrong</h1>
          <p className="mt-3 text-sm text-slate-600">
            The page hit an unexpected error and could not finish loading. Please refresh — if it keeps happening,
            contact the site administrator.
          </p>

          {process.env.NODE_ENV !== "production" && this.state.error && (
            <pre className="mt-4 overflow-auto rounded bg-slate-100 p-3 text-left text-[11px] text-rose-700">
              {String(this.state.error?.message || this.state.error)}
            </pre>
          )}

          <div className="mt-6 flex justify-center gap-3">
            <button
              type="button"
              onClick={this.handleReset}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
              data-testid="error-refresh"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh page
            </button>
            <Link
              to="/"
              onClick={this.handleReset}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              data-testid="error-home"
            >
              <Home className="h-3.5 w-3.5" /> Go home
            </Link>
          </div>
        </div>
      </div>
    );
  }
}
